import { message } from 'antd';
import { STORAGE_KEYS } from './constants';

export const storage = {
  get: (key: string): any => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return localStorage.getItem(key);
    }
  },

  set: (key: string, value: any): void => {
    try {
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },

  remove: (key: string): void => {
    localStorage.removeItem(key);
  },

  clear: (): void => {
    localStorage.clear();
  },

  getToken: (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  setToken: (token: string): void => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  },

  removeToken: (): void => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  getUser: (): any => {
    return storage.get(STORAGE_KEYS.USER_DATA);
  },

  setUser: (user: any): void => {
    storage.set(STORAGE_KEYS.USER_DATA, user);
  },

  clearAuth: (): void => {
    storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    storage.remove(STORAGE_KEYS.USER_DATA);
  },
};

export const notify = {
  success: (content: string, duration: number = 3): void => {
    message.success(content, duration);
  },

  error: (content: string, duration: number = 5): void => {
    message.error(content, duration);
  },

  warning: (content: string, duration: number = 4): void => {
    message.warning(content, duration);
  },

  info: (content: string, duration: number = 3): void => {
    message.info(content, duration);
  },

  loading: (content: string = 'Loading...', duration: number = 0): () => void => {
    const hide = message.loading(content, duration);
    return hide;
  },
};

export const url = {
  getQueryParams: (search: string): Record<string, string> => {
    const params = new URLSearchParams(search);
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  },

  buildQueryString: (params: Record<string, any>): string => {
    const filteredParams = Object.entries(params)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`);
    
    return filteredParams.length > 0 ? `?${filteredParams.join('&')}` : '';
  },

  addQueryParams: (baseUrl: string, params: Record<string, any>): string => {
    const queryString = url.buildQueryString(params);
    return `${baseUrl}${queryString}`;
  },
};

export const array = {
  unique: <T>(arr: T[], key?: keyof T): T[] => {
    if (!key) {
      return [...new Set(arr)];
    }
    const seen = new Set();
    return arr.filter(item => {
      const keyValue = item[key];
      if (seen.has(keyValue)) {
        return false;
      }
      seen.add(keyValue);
      return true;
    });
  },

  groupBy: <T>(arr: T[], key: keyof T): Record<string, T[]> => {
    return arr.reduce((result, item) => {
      const groupKey = String(item[key]);
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(item);
      return result;
    }, {} as Record<string, T[]>);
  },

  chunk: <T>(arr: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  },
};

export const object = {
  deepClone: <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
  },

  omit: <T extends Record<string, any>>(obj: T, keys: string[]): Partial<T> => {
    const result = { ...obj };
    keys.forEach(key => {
      delete result[key];
    });
    return result;
  },

  pick: <T extends Record<string, any>>(obj: T, keys: string[]): Partial<T> => {
    const result: Partial<T> = {};
    keys.forEach(key => {
      if (key in obj) {
        result[key as keyof T] = obj[key];
      }
    });
    return result;
  },

  isEmpty: (obj: any): boolean => {
    if (!obj) return true;
    if (Array.isArray(obj)) return obj.length === 0;
    if (typeof obj === 'object') return Object.keys(obj).length === 0;
    return false;
  },
};

export const download = {
  file: (data: Blob, filename: string): void => {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  json: (data: any, filename: string): void => {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    download.file(blob, `${filename}.json`);
  },

  csv: (data: any[], filename: string): void => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      ),
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    download.file(blob, `${filename}.csv`);
  },
};

export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    notify.success('Copied to clipboard');
    return true;
  } catch {
    notify.error('Failed to copy to clipboard');
    return false;
  }
};

export const generateId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const colors = {
  getRandomColor: (): string => {
    const colors = [
      '#1890ff', '#52c41a', '#faad14', '#f5222d',
      '#722ed1', '#13c2c2', '#eb2f96', '#fa541c',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  },

  hexToRgba: (hex: string, alpha: number = 1): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  },
};

export { UserPermissions as permissions } from 'types/user.types';
